import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Instagram, Mail, MapPin } from 'lucide-react';
import { Logo } from './ui/Logo';
import { Button } from './ui/Button';
import { useScreenInit } from '../useScreenInit';
const NAV_LINKS = [{
  name: 'Accueil',
  path: '/'
}, {
  name: 'Services',
  path: '/services'
}, {
  name: 'À propos',
  path: '/about'
}, {
  name: 'Portfolio',
  path: '/portfolio'
}];
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  useScreenInit();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return <div className="min-h-screen flex flex-col font-sans bg-[#FFF8F2]" data-id="element-13">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#FFF8F2]/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-14">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-15">
          <Logo data-id="element-16" />

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8 bg-white/80 backdrop-blur-sm px-8 py-3 rounded-full shadow-sm" data-id="element-17">
            {NAV_LINKS.map(link => <Link key={link.path} to={link.path} className={`text-sm font-bold tracking-wide transition-colors hover:text-[#FF6B9D] ${location.pathname === link.path ? 'text-[#FF6B9D]' : 'text-[#2D2D2D]'}`} data-id="element-18">
                {link.name}
              </Link>)}
            <Button href="/contact" variant="pink" size="sm" className="ml-2" data-id="element-19">
              Contact
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden p-2 text-[#2D2D2D] hover:text-[#FF6B9D] transition-colors bg-white rounded-full shadow-sm" onClick={() => setIsMobileMenuOpen(true)} aria-label="Ouvrir le menu" data-id="element-20">
            <Menu size={24} data-id="element-21" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-22">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[60] bg-[#FFD34D] flex flex-col" data-id="element-23">
            <div className="flex items-center justify-between p-6" data-id="element-24">
              <Logo onClick={() => setIsMobileMenuOpen(false)} data-id="element-25" />
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-[#2D2D2D] hover:text-white transition-colors bg-white/50 rounded-full" data-id="element-26">
                <X size={32} data-id="element-27" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center space-y-8 p-6" data-id="element-28">
              {NAV_LINKS.map(link => <Link key={link.path} to={link.path} className={`text-4xl font-display transition-colors ${location.pathname === link.path ? 'text-white' : 'text-[#2D2D2D]'}`} data-id="element-29">
                  {link.name}
                </Link>)}
              <div className="pt-8" data-id="element-30">
                <Button href="/contact" variant="pink" size="lg" data-id="element-31">
                  Nous contacter
                </Button>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1" data-id="element-32">{children}</main>

      {/* Footer */}
      <footer className="bg-[#FFF8F2] pt-20 pb-10 mt-auto" data-id="element-33">
        <div className="container mx-auto px-6 md:px-12" data-id="element-34">
          <div className="bg-[#BDE1FF] rounded-[3rem] p-10 md:p-16 mb-12 shadow-sm" data-id="element-35">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12" data-id="element-36">
              <div className="col-span-1 md:col-span-1 flex flex-col items-start" data-id="element-37">
                <div className="bg-white p-6 rounded-3xl mb-6 inline-block shadow-sm" data-id="element-38">
                  <Logo data-id="element-39" />
                </div>
                <p className="text-[#2D2D2D]/80 text-sm max-w-xs font-medium" data-id="element-40">
                  Connecting talents. Creating impact. L'agence qui donne vie à
                  vos campagnes d'influence.
                </p>
              </div>

              <div data-id="element-41">
                <h4 className="font-display text-3xl mb-6 text-[#FF6B9D]" data-id="element-42">
                  Navigation
                </h4>
                <ul className="space-y-3 font-medium" data-id="element-43">
                  {NAV_LINKS.map(link => <li key={link.path} data-id="element-44">
                      <Link to={link.path} className="text-[#2D2D2D]/80 hover:text-[#FF6B9D] transition-colors" data-id="element-45">
                        {link.name}
                      </Link>
                    </li>)}
                  <li data-id="element-46">
                    <Link to="/contact" className="text-[#2D2D2D]/80 hover:text-[#FF6B9D] transition-colors" data-id="element-47">
                      Contact
                    </Link>
                  </li>
                </ul>
              </div>

              <div data-id="element-48">
                <h4 className="font-display text-3xl mb-6 text-[#FF8A3D]" data-id="element-49">
                  Contact
                </h4>
                <ul className="space-y-4 font-medium" data-id="element-50">
                  <li data-id="element-51">
                    <a href="mailto:janyagency.m@gmail.com" className="flex items-center text-[#2D2D2D]/80 hover:text-[#FF8A3D] transition-colors" data-id="element-52">
                      <Mail size={18} className="mr-3 text-[#FF8A3D]" data-id="element-53" />
                      janyagency.m@gmail.com
                    </a>
                  </li>
                  <li className="flex items-center text-[#2D2D2D]/80" data-id="element-54">
                    <MapPin size={18} className="mr-3 text-[#FF8A3D]" data-id="element-55" />
                    Paris | Worldwide
                  </li>
                </ul>
              </div>

              <div data-id="element-56">
                <h4 className="font-display text-3xl mb-6 text-[#FFD34D]" data-id="element-57">
                  Social
                </h4>
                <a href="https://instagram.com/janyagency" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white hover:bg-[#FFD34D] text-[#2D2D2D] transition-all duration-300 hover:-translate-y-1 shadow-sm" data-id="element-58">
                  <Instagram size={24} data-id="element-59" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between text-xs text-[#2D2D2D]/50 font-medium px-4" data-id="element-60">
            <p data-id="element-61">
              &copy; {new Date().getFullYear()} JANY AND CO, SAS. Tous droits
              réservés.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0" data-id="element-62">
              <Link to="/contact" className="hover:text-[#FF6B9D] transition-colors" data-id="element-63">
                Mentions légales
              </Link>
              <Link to="/contact" className="hover:text-[#FF6B9D] transition-colors" data-id="element-64">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}